/**
 * Enhanced Theme Provider with custom themes and seasonal variations
 * Provides theme context and utilities throughout the application
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { Appearance, ColorSchemeName } from 'react-native';
import {
  colorTokens,
  typographyTokens,
  spacingTokens,
  motionTokens,
} from '../tokens';
import { Theme, ThemeMode, ThemeContextValue, ThemeColorScheme } from './types';
import { validateTheme } from './validation';
import { tokenStorage } from '@coffeeplexity/shared';

// Create theme context
const ThemeContext = createContext<ThemeContextValue | null>(null);

// Seasonal theme variations
const SEASONAL_THEMES = {
  default: {
    light: {},
    dark: {},
  },
  winter: {
    light: {
      primary: {
        500: '#3b82f6', // Blue
        600: '#2563eb',
        700: '#1d4ed8',
      },
    },
    dark: {
      primary: {
        500: '#60a5fa',
        600: '#3b82f6',
        700: '#2563eb',
      },
    },
  },
  spring: {
    light: {
      primary: {
        500: '#10b981', // Green
        600: '#059669',
        700: '#047857',
      },
    },
    dark: {
      primary: {
        500: '#34d399',
        600: '#10b981',
        700: '#059669',
      },
    },
  },
  summer: {
    light: {
      primary: {
        500: '#f59e0b', // Amber
        600: '#d97706',
        700: '#b45309',
      },
    },
    dark: {
      primary: {
        500: '#fbbf24',
        600: '#f59e0b',
        700: '#d97706',
      },
    },
  },
  autumn: {
    light: {
      primary: {
        500: '#d97706', // Orange
        600: '#b45309',
        700: '#92400e',
      },
    },
    dark: {
      primary: {
        500: '#fb923c',
        600: '#d97706',
        700: '#b45309',
      },
    },
  },
} as const;

// Custom theme presets
const CUSTOM_THEMES = {
  coffee: {
    light: {
      primary: {
        500: '#e8944a',
        600: '#d4732f',
        700: '#b85c2a',
      },
    },
    dark: {
      primary: {
        500: '#f4b968',
        600: '#e8944a',
        700: '#d4732f',
      },
    },
  },
  minimal: {
    light: {
      primary: {
        500: '#525252',
        600: '#404040',
        700: '#262626',
      },
    },
    dark: {
      primary: {
        500: '#a3a3a3',
        600: '#737373',
        700: '#525252',
      },
    },
  },
  warm: {
    light: {
      primary: {
        500: '#f97316',
        600: '#ea580c',
        700: '#c2410c',
      },
    },
    dark: {
      primary: {
        500: '#fb923c',
        600: '#f97316',
        700: '#ea580c',
      },
    },
  },
  cool: {
    light: {
      primary: {
        500: '#06b6d4',
        600: '#0891b2',
        700: '#0e7490',
      },
    },
    dark: {
      primary: {
        500: '#22d3ee',
        600: '#06b6d4',
        700: '#0891b2',
      },
    },
  },
} as const;

/**
 * Create theme object from mode and customizations
 */
function createTheme(
  mode: ThemeMode,
  systemColorScheme: ColorSchemeName,
  seasonalTheme: string = 'default',
  customTheme: string = 'coffee'
): Theme {
  // Determine effective theme mode
  let effectiveMode: 'light' | 'dark';

  if (mode === 'auto') {
    effectiveMode = systemColorScheme === 'dark' ? 'dark' : 'light';
  } else {
    effectiveMode = mode;
  }

  // Get base colors for the effective mode
  const baseColors = colorTokens.themes[effectiveMode];
  
  // Apply seasonal theme variations
  const seasonalVariations = SEASONAL_THEMES[seasonalTheme as keyof typeof SEASONAL_THEMES]?.[effectiveMode] || {};
  
  // Apply custom theme variations
  const customVariations = CUSTOM_THEMES[customTheme as keyof typeof CUSTOM_THEMES]?.[effectiveMode] || {};

  // Merge colors with customizations
  const colors = {
    ...baseColors,
    ...seasonalVariations,
    ...customVariations,
  } as ThemeColorScheme;

  return {
    mode,
    colors,
    typography: typographyTokens,
    spacing: {
      spacing: spacingTokens.spacing,
      borderRadius: spacingTokens.borderRadius,
      touchTargets: spacingTokens.touchTargets,
      elevation: spacingTokens.elevation,
    },
    motion: motionTokens,
  };
}

/**
 * Theme Provider Props
 */
interface ThemeProviderProps {
  children: React.ReactNode;
  initialMode?: ThemeMode;
  onThemeChange?: (theme: Theme) => void;
  validateOnChange?: boolean;
}

/**
 * Enhanced Theme Provider Component
 */
export function ThemeProvider({
  children,
  initialMode = 'auto',
  onThemeChange,
  validateOnChange = true,
}: ThemeProviderProps) {
  const [mode, setMode] = useState<ThemeMode>(initialMode);
  const [systemColorScheme, setSystemColorScheme] = useState<ColorSchemeName>(
    Appearance.getColorScheme()
  );
  const [seasonalTheme, setSeasonalTheme] = useState('default');
  const [customTheme, setCustomTheme] = useState('coffee');
  const [isLoading, setIsLoading] = useState(true);

  // Create current theme
  const theme = createTheme(mode, systemColorScheme, seasonalTheme, customTheme);

  // Load saved preferences
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const savedMode = await tokenStorage.getToken('theme_mode');
        const savedSeasonal = await tokenStorage.getToken('seasonal_theme');
        const savedCustom = await tokenStorage.getToken('custom_theme');
        
        if (savedMode) setMode(savedMode as ThemeMode);
        if (savedSeasonal) setSeasonalTheme(savedSeasonal as string);
        if (savedCustom) setCustomTheme(savedCustom as string);
      } catch (error) {
        console.warn('Failed to load theme preferences:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, []);

  // Listen to system color scheme changes
  useEffect(() => {
    const subscription = Appearance.addChangeListener(({ colorScheme }) => {
      setSystemColorScheme(colorScheme);
    });

    return () => subscription?.remove();
  }, []);

  // Validate theme when it changes
  useEffect(() => {
    if (validateOnChange && !isLoading) {
      const validation = validateTheme(theme);
      if (!validation.isValid) {
        console.warn('Theme validation failed:', validation.errors);
      }
      if (validation.warnings.length > 0) {
        console.warn('Theme validation warnings:', validation.warnings);
      }
    }

    onThemeChange?.(theme);
  }, [theme, onThemeChange, validateOnChange, isLoading]);

  // Theme utilities
  const toggleMode = useCallback(() => {
    setMode(current => {
      if (current === 'auto') return 'light';
      if (current === 'light') return 'dark';
      return 'auto';
    });
  }, []);

  const setSeasonalThemeWithPersistence = useCallback(async (newSeasonalTheme: string) => {
    try {
      setSeasonalTheme(newSeasonalTheme);
      await tokenStorage.setToken('seasonal_theme', newSeasonalTheme);
    } catch (error) {
      console.error('Failed to save seasonal theme:', error);
    }
  }, []);

  const setCustomThemeWithPersistence = useCallback(async (newCustomTheme: string) => {
    try {
      setCustomTheme(newCustomTheme);
      await tokenStorage.setToken('custom_theme', newCustomTheme);
    } catch (error) {
      console.error('Failed to save custom theme:', error);
    }
  }, []);

  const isDark = systemColorScheme === 'dark' || mode === 'dark';
  const isLight = !isDark;

  // Context value
  const contextValue: ThemeContextValue = {
    theme,
    mode,
    setMode,
    toggleMode,
    isDark,
    isLight,
    seasonalTheme,
    setSeasonalTheme: setSeasonalThemeWithPersistence,
    customTheme,
    setCustomTheme: setCustomThemeWithPersistence,
    isLoading,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to use theme context
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}

/**
 * Hook to get current theme colors
 */
export function useThemeColors() {
  const { theme } = useTheme();
  return theme.colors;
}

/**
 * Hook to get current theme typography
 */
export function useThemeTypography() {
  const { theme } = useTheme();
  return theme.typography;
}

/**
 * Hook to get current theme spacing
 */
export function useThemeSpacing() {
  const { theme } = useTheme();
  return theme.spacing;
}

/**
 * Hook to get current theme motion
 */
export function useThemeMotion() {
  const { theme } = useTheme();
  return theme.motion;
}
